import { FC, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  selectActiveCompany,
  selectPrimaryUser,
  selectUsersData,
} from '@features/workspace/store/selectors';
import { setPrimaryUser } from '@features/workspace/store/actions';
import { toast } from 'react-toastify';
import Toast from '@components/ui/Toast';

interface ChangePrimaryUserDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChangePrimaryUserDialog: FC<ChangePrimaryUserDialogProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const allUsers = useAppSelector(selectUsersData);
  const company = useAppSelector(selectActiveCompany);
  const currentPrimaryUser = useAppSelector(selectPrimaryUser);

  const [newPrimaryUserId, setNewPrimaryUserId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const userOptions = useMemo(() => {
    return allUsers.filter((user) => user.id !== currentPrimaryUser?.id);
  }, [allUsers, currentPrimaryUser]);

  const handleClose = () => {
    if (isLoading) return;
    setNewPrimaryUserId('');
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setNewPrimaryUserId('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!company || !newPrimaryUserId) return;
    setIsLoading(true);
    try {
      await dispatch(
        setPrimaryUser({ companyId: company.id, userId: newPrimaryUserId })
      ).unwrap();
      const newUser = allUsers.find((u) => u.id === newPrimaryUserId);
      const newUserName = `${newUser?.person.firstName} ${newUser?.person.lastName}`;

      toast(
        <Toast
          showCloseButton
          text={`Primary user changed to ${newUserName}`}
          title="Primary user changed successfully"
          variant="success"
        />,
        {
          hideProgressBar: true,
          autoClose: 5000,
          closeButton: false,
        }
      );
      handleClose();
    } catch (error) {
      toast(
        <Toast
          showCloseButton
          text="Failed to change primary user."
          title="Oh no, something went wrong"
          variant="critical"
        />,
        {
          hideProgressBar: true,
          autoClose: 5000,
          closeButton: false,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>Change primary user</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{ position: 'absolute', right: 8, top: 8 }}
        onClick={handleClose}
      >
        <i className="ri-close-line" />
      </IconButton>
      <DialogContent>
        <Typography gutterBottom>
          You are about to change the primary user. This user will now have the
          ability to approve and reject payment requests.
        </Typography>
        <TextField
          fullWidth
          select
          disabled={isLoading || userOptions.length === 0}
          label="New primary user"
          margin="normal"
          SelectProps={{
            displayEmpty: true,
          }}
          value={newPrimaryUserId}
          onChange={(e) => setNewPrimaryUserId(e.target.value)}
        >
          <MenuItem disabled value="">
            <em>
              {userOptions.length > 0
                ? 'Select a user'
                : 'No other users available'}
            </em>
          </MenuItem>
          {userOptions.map((user) => (
            <MenuItem key={user.id} value={user.id}>
              {`${user.person.firstName} ${user.person.lastName} (${user.email})`}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          fullWidth
          color="secondary"
          disabled={isLoading}
          size="large"
          variant="contained"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          disabled={isLoading || !newPrimaryUserId || userOptions.length === 0}
          size="large"
          variant="contained"
          onClick={handleSave}
        >
          {isLoading ? 'Changing...' : 'Change primary user'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePrimaryUserDialog;
